<template>
  <div class="relative">
    <button 
      @click="togglePopover"
      class="btn btn-secondary relative"
    >
      <span class="sr-only">Notifications</span>
      <BellIcon class="w-5 h-5" />
      <span 
        v-if="unreadCount > 0"
        class="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center"
      >
        {{ unreadCount }}
      </span>
    </button>

    <!-- Notifications Popover -->
    <div 
      v-if="isOpen"
      class="absolute right-0 mt-2 w-96 bg-dark-100 border border-secondary-800 rounded-lg shadow-lg py-2 z-50"
    >
      <div class="px-4 py-2 border-b border-secondary-800 flex justify-between items-center">
        <h3 class="font-medium">Notifications</h3>
        <button 
          v-if="notifications?.some(n => !n.read)"
          @click="markAllAsRead"
          class="text-sm text-primary-400 hover:text-primary-300"
        >
          Mark all as read
        </button>
      </div>

      <div class="max-h-96 overflow-y-auto">
        <div v-if="isLoading && (!notifications || !notifications.length)" class="px-4 py-3 text-gray-400 text-center">
          Loading notifications...
        </div>
        <div v-else-if="error" class="px-4 py-3 text-red-400 text-center">
          {{ error }}
          <button 
            @click="refresh"
            class="text-primary-400 hover:text-primary-300 ml-2"
          >
            Retry
          </button>
        </div>
        <div v-else-if="!notifications?.length" class="px-4 py-3 text-gray-400 text-center">
          No notifications
        </div>
        <template v-else>
          <div 
            v-for="notification in notifications" 
            :key="notification.id"
            class="px-4 py-3 hover:bg-dark-200 cursor-pointer"
            :class="{ 'bg-dark-200/50': !notification.read }"
            @click="handleNotificationClick(notification)"
          >
            <div class="flex justify-between items-start">
              <div class="flex-1">
                <p class="text-sm font-medium" :class="{ 'text-white': !notification.read, 'text-gray-300': notification.read }">
                  {{ notification.title }}
                </p>
                <p class="text-sm text-gray-400 mt-1 whitespace-pre-line">{{ notification.description }}</p>
                <div class="flex items-center space-x-4 mt-1">
                  <p class="text-xs text-gray-500">{{ formatDate(notification.createdAt) }}</p>
                  <router-link 
                    v-if="notification.testRun"
                    :to="`/test-execution/${getNumericId(notification.testRunId)}/${notification.testRun.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`"
                    class="text-xs text-primary-400 hover:text-primary-300"
                    @click.stop
                  >
                    View Test Run
                  </router-link>
                </div>
              </div>
              <div v-if="!notification.read" class="w-2 h-2 bg-primary-500 rounded-full mt-2"></div>
            </div>
          </div>
        </template>
      </div>

      <div class="px-4 py-2 border-t border-secondary-800">
        <router-link 
          to="/notifications"
          class="text-sm text-primary-400 hover:text-primary-300 block text-center"
          @click="isOpen = false"
        >
          View all notifications
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue';
import { BellIcon } from '@heroicons/vue/24/outline';
import { useNotificationsStore } from '@/stores/notificationsStore';
import { useRouter } from 'vue-router';
import { formatDistanceToNow } from 'date-fns';
import { useAuthStore } from '@/stores/auth';

const router = useRouter();
const notificationsStore = useNotificationsStore();
const authStore = useAuthStore();
const isOpen = ref(false);
let refreshInterval = null;

const { 
  notifications,
  unreadCount,
  isLoading,
  error,
  fetchNotifications,
  markAsRead,
  markAllAsRead: markAllNotificationsAsRead,
  refresh
} = notificationsStore;

// Single onMounted hook
onMounted(() => {
  // Add click outside listener
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  // Clean up event listener and interval
  document.removeEventListener('click', handleClickOutside);
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }
});

// Watch for both auth and initialization state
watch(
  () => ({
    isAuthenticated: authStore.isAuthenticated,
    initialized: authStore.initialized
  }),
  async (newState, oldState) => {
    // Only proceed if both initialized and authenticated
    if (newState.initialized && newState.isAuthenticated) {
      // Force fetch notifications
      await fetchNotifications().catch(err => {
        console.error('NotificationsPopover: Error fetching notifications:', err);
      });
    } else if (!newState.isAuthenticated && oldState?.isAuthenticated) {
      if (refreshInterval) {
        clearInterval(refreshInterval);
        refreshInterval = null;
      }
      isOpen.value = false;
    }
  },
  { immediate: true, deep: true }
);

// Watch for popover open/close to manage refresh interval
watch(isOpen, (newValue) => {  
  if (newValue && authStore.initialized && authStore.isAuthenticated) {
    // Always fetch when opening the popover
    fetchNotifications().catch(err => {
      console.error('Error fetching notifications:', err);
    });
    startRefreshInterval();
  } else {
    if (refreshInterval) {
      clearInterval(refreshInterval);
      refreshInterval = null;
    }
  }
});

function startRefreshInterval() {
  if (refreshInterval) {
    clearInterval(refreshInterval);
  }
  refreshInterval = setInterval(() => {
    if (authStore.initialized && authStore.isAuthenticated && isOpen.value) {
      fetchNotifications().catch(err => {
        console.error('Error in refresh interval:', err);
      });
    }
  }, 30000); // Refresh every 30 seconds
}

function togglePopover(event) {
  event.stopPropagation();
  isOpen.value = !isOpen.value;
  
  if (isOpen.value && authStore.isAuthenticated) {
    fetchNotifications().catch(err => {
      console.error('NotificationsPopover: Error fetching notifications:', err);
    });
  }
}

function handleClickOutside(event) {
  const popover = event.target.closest('.relative');
  if (!popover) {
    isOpen.value = false;
  }
}

async function handleNotificationClick(notification) {  
  if (!notification.read) {
    try {
      await markAsRead(notification.id);
    } catch (err) {
      console.error('NotificationsPopover: Error marking as read:', err);
    }
  }

  // Handle navigation based on notification type
  if ((notification.type === 'test_run_completed' || notification.type === 'comment_mention') && notification.testRun) {
    const slug = notification.testRun.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    router.push(`/test-execution/${getNumericId(notification.testRunId)}/${slug}`);
  }

  isOpen.value = false;
}

async function markAllAsRead() {
  try {
    await markAllNotificationsAsRead();
  } catch (err) {
    console.error('NotificationsPopover: Error marking all as read:', err);
  }
}

function formatDate(date) {
  return formatDistanceToNow(new Date(date), { addSuffix: true });
}

// Function to generate a numeric ID from UUID
const getNumericId = (uuid) => {
  return parseInt(uuid.slice(0, 8), 16);
}
</script> 